import React from 'react';
import {Link, useParams} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AreasSection from '../components/AreasSection';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const {FiHome, FiCheck, FiPhone, FiCalendar, FiStar, FiUser, FiShield, FiClock, FiTarget, FiDollarSign, FiAward, FiCheckCircle} = FiIcons;

const EndOfTenancyPage = () => {
  const {area} = useParams();
  const currentArea = area ? formatAreaName(area) : 'Aylesbury';

  const handleLinkClick = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const comprehensiveChecklist = [
    {
      area: "Kitchen Deep Clean",
      tasks: [
        "Inside & outside of ALL appliances (oven, hob, microwave, fridge, dishwasher)",
        "Complete oven deep clean including racks, trays, and glass door",
        "Extractor fan and filters thoroughly degreased",
        "All cupboards and drawers cleaned inside and out",
        "Worktops deep cleaned and sanitized",
        "Tiles, grout, and splash-backs scrubbed to remove all stains",
        "Sink descaled and taps polished to shine",
        "Floor deep cleaned including behind appliances",
        "Light fittings and switches cleaned"
      ]
    },
    {
      area: "Bathroom Perfection",
      tasks: [
        "Toilet deep cleaned inside and out including behind and around base",
        "Bath and shower completely descaled and sanitized",
        "Shower screen/curtain cleaned or replaced if necessary",
        "All tiles and grout restored to original condition",
        "Mirrors polished to streak-free finish",
        "Chrome fixtures descaled and polished",
        "Extractor fan cleaned and filters replaced",
        "Medicine cabinet and storage cleaned inside",
        "Floor deep cleaned including edges and corners"
      ]
    },
    {
      area: "Living Areas & Bedrooms",
      tasks: [
        "All carpets professionally cleaned or deep vacuumed",
        "Hard floors mopped and polished to original shine",
        "ALL windows cleaned inside (outside if ground floor)",
        "Skirting boards and architraves wiped down",
        "Light switches, sockets, and door handles sanitized",
        "Built-in wardrobes cleaned inside and out",
        "Radiators cleaned front and back",
        "Curtain rails and blinds dusted",
        "Picture hooks removed and holes filled (if requested)"
      ]
    },
    {
      area: "Final Touches",
      tasks: [
        "All internal doors cleaned both sides",
        "Door frames and architraves wiped clean",
        "Light fixtures and lampshades dusted",
        "Cobwebs removed from all corners and ceilings",
        "Window sills and frames thoroughly cleaned",
        "Balcony or patio cleaning (if applicable)",
        "Final walk-through inspection",
        "Touch-up cleaning of any missed areas"
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: FiAward,
      title: "98% Success Rate",
      description: "98% of our clients get their full deposit back - that's our track record!"
    },
    {
      icon: FiCheckCircle,
      title: "Letting Agent Approved",
      description: "We work with major letting agents and know exactly what they expect"
    },
    {
      icon: FiShield,
      title: "Risk-Free Guarantee",
      description: "If you don't get your deposit back due to cleaning, we'll refund our fee"
    },
    {
      icon: FiClock,
      title: "Same Day Service",
      description: "Need it done urgently? We can often accommodate same-day bookings"
    }
  ];

  const successStories = [
    {
      name: "Emma Wilson",
      property: "2-bed flat in Aylesbury",
      deposit: "£1,400",
      rating: 5,
      text: `Absolutely incredible service! I was worried about getting my deposit back from a very picky landlord in ${currentArea}. Diamond Cleaning not only met every requirement but exceeded them. Got my full £1,400 deposit back within a week!`,
      date: "1 week ago"
    },
    {
      name: "James Carter",
      property: "3-bed house in Milton Keynes",
      deposit: "£2,100",
      rating: 5,
      text: `Best decision I made when moving out! The team in ${currentArea} was thorough beyond belief. They cleaned areas I didn't even know existed. Landlord was so impressed, we got our deposit back same day!`,
      date: "2 weeks ago"
    },
    {
      name: "Sophie Mitchell",
      property: "1-bed apartment in Buckingham",
      deposit: "£950",
      rating: 5,
      text: `Professional team that truly understands what landlords expect. They provided detailed photos of their work and a comprehensive checklist. Zero deductions from our deposit - couldn't be happier!`,
      date: "3 weeks ago"
    }
  ];

  // Enhanced SEO schema
  const endOfTenancySchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `End of Tenancy Cleaning ${currentArea}`,
    "description": `Professional end of tenancy cleaning in ${currentArea} with deposit back guarantee. Comprehensive cleaning to landlord standards. 98% success rate.`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Diamond Cleaning Aylesbury"
    },
    "areaServed": currentArea,
    "offers": {
      "@type": "Offer",
      "price": "18",
      "priceCurrency": "GBP",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "18",
        "priceCurrency": "GBP",
        "unitText": "per hour"
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`End of Tenancy Cleaning ${currentArea} - Deposit Back Guarantee | Diamond Cleaning`}
        description={`Professional end of tenancy cleaning in ${currentArea} with deposit back guarantee. 98% success rate. Comprehensive cleaning to landlord standards. Book online or call 07878 522378`}
        keywords={`end of tenancy cleaning ${currentArea}, deposit back guarantee, move out cleaning ${currentArea}, landlord cleaning standards, letting agent cleaning, professional end of tenancy, ${currentArea} move out clean`}
        schema={endOfTenancySchema}
      />
      <Header currentArea={currentArea} />

      {/* Enhanced Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 bg-cover bg-center" 
             style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/img/domestic-1.webp')`}} />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <div className="bg-green-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 inline-block">
              98% DEPOSIT SUCCESS RATE
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              End of Tenancy Cleaning {currentArea}
            </h1>
            
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 leading-relaxed max-w-4xl mx-auto">
              <strong>Professional End of Tenancy Cleaning!</strong> Comprehensive cleaning service that meets landlord and letting agent standards. Professional service with photographic evidence and detailed checklist.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="grid md:grid-cols-3 gap-4 text-white">
                <div className="text-center">
                  <SafeIcon icon={FiTarget} className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">98% Success Rate</div>
                  <div className="text-sm opacity-90">Deposits returned</div>
                </div>
                <div className="text-center">
                  <SafeIcon icon={FiShield} className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">Money Back</div>
                  <div className="text-sm opacity-90">If deposit not returned</div>
                </div>
                <div className="text-center">
                  <SafeIcon icon={FiClock} className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">Same Day</div>
                  <div className="text-sm opacity-90">Service available</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/book" 
                className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl"
                onClick={handleLinkClick}
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                <span>Book Professional Clean</span>
              </Link>
              <a 
                href="tel:07878522378"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                <span>Call: 07878 522378</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why 98% of Our Clients Get Their Full Deposit Back
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We don't just clean - we guarantee results. Here's what sets us apart from other cleaning companies in {currentArea}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <SafeIcon icon={item.icon} className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Checklist */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Comprehensive End of Tenancy Checklist
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every single item on this list will be completed to professional standards. No shortcuts, no missed areas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {comprehensiveChecklist.map((section, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  {section.area}
                </h3>
                <div className="space-y-3">
                  {section.tasks.map((task, taskIndex) => (
                    <div key={taskIndex} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{task}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Transparent End of Tenancy Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              No hidden fees. No surprises. Just honest pricing for professional results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
              <SafeIcon icon={FiHome} className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Studio/1 Bed</h3>
              <p className="text-gray-600 mb-4">Perfect for smaller properties</p>
              <div className="text-3xl font-bold text-blue-600 mb-2">From £300</div>
              {/* <div className="text-lg text-gray-600 mb-6">£72 - £108 total</div> */}
              <ul className="text-sm text-gray-600 space-y-2 mb-6 text-left">
                <li>Complete property deep clean</li>
                <li>All appliances included</li>
                {/* <li>Carpet cleaning</li>
                <li>Window cleaning (inside)</li>
                <li>Deposit back guarantee</li> */}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center shadow-lg border-2 border-green-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                MOST POPULAR
              </div>
              <SafeIcon icon={FiHome} className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">2-3 Bedrooms</h3>
              <p className="text-gray-600 mb-4">Perfect for family homes</p>
              <div className="text-3xl font-bold text-green-600 mb-2">From £400</div>
              {/* <div className="text-lg text-gray-600 mb-6">£108 - £162 total</div> */}
              <ul className="text-sm text-gray-600 space-y-2 mb-6 text-left">
                <li>Comprehensive deep clean</li>
                <li>All rooms and bathrooms</li>
                <li>Kitchen appliance deep clean</li>
                <li>Detailed finishing touches</li>
                <li>Photographic evidence</li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
              <SafeIcon icon={FiHome} className="w-16 h-16 text-purple-600 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">4+ Bedrooms</h3>
              <p className="text-gray-600 mb-4">Large family homes</p>
              <div className="text-3xl font-bold text-purple-600 mb-2">From £500</div>
              {/* <div className="text-lg text-gray-600 mb-6">£162 - £270 total</div> */}
              <ul className="text-sm text-gray-600 space-y-2 mb-6 text-left">
                <li>Full house deep clean</li>
                <li>Multiple bathrooms</li>
                <li>Extensive kitchen cleaning</li>
                {/* <li>Garden/patio cleaning</li> */}
                <li>Priority booking available</li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="bg-blue-50 rounded-2xl p-6 max-w-2xl mx-auto">
              <p className="text-gray-700 font-medium mb-4">
                <strong>Pro Tip:</strong> Book 48 hours in advance for guaranteed availability and 10% early bird discount!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/book" 
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center space-x-2"
                  onClick={handleLinkClick}
                >
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Get Instant Quote</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Real Deposit Success Stories from {currentArea}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it - see how we've helped hundreds of tenants get their full deposits back
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div key={index} className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{story.name}</h4>
                    <p className="text-sm text-gray-600">{story.property}</p>
                  </div>
                </div>
                <div className="bg-green-600 text-white px-4 py-2 rounded-lg mb-4 text-center">
                  <div className="font-bold">FULL DEPOSIT RETURNED</div>
                  <div className="text-lg">{story.deposit}</div>
                </div>
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 mr-2">
                    {[...Array(story.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">{story.date}</span>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{story.text}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <div className="bg-gray-100 rounded-2xl p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Join Our Success Stories!</h3>
              <p className="text-gray-600 mb-6">
                Over 500 successful deposit returns in {currentArea} and surrounding areas. Your success story could be next!
              </p>
              <a 
                href="https://share.google/xr3mZOVXjGPtaFHeP" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                <span>Read All Our Reviews</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Secure Your Full Deposit Return?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Don't risk losing hundreds of pounds from your deposit. Book our professional end of tenancy cleaning service and move out with confidence.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center text-white">
                <SafeIcon icon={FiClock} className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Same Day Available</h3>
                <p className="text-green-100">Last minute? We can help!</p>
              </div>
              <div className="text-center text-white">
                <SafeIcon icon={FiShield} className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Money Back Promise</h3>
                <p className="text-green-100">Risk-free guarantee</p>
              </div>
              <div className="text-center text-white">
                <SafeIcon icon={FiTarget} className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">98% Success Rate</h3>
                <p className="text-green-100">Proven track record</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/book" 
              className="bg-white text-green-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 shadow-xl"
              onClick={handleLinkClick}
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Book Professional Clean Now</span>
            </Link>
            <a 
              href="tel:07878522378"
              className="bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2 border-2 border-white shadow-xl"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Call: 07878 522378</span>
            </a>
          </div>
        </div>
      </section>

      <AreasSection currentArea={currentArea} />
      <Footer />
    </div>
  );
};

const formatAreaName = (area) => {
  return area
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default EndOfTenancyPage;